// components/legal/refund-policy.jsx
"use client";

import React, { useMemo } from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import { getStrings, interpolate } from "../../i18n/dictionaries";
import { getLegalProductContent } from "@/i18n/legalProductContent";

export default function RefundPolicy() {
  const { locale } = useLanguage();
  const t = useMemo(() => getStrings(locale, "legal"), [locale]);
  const tc = useMemo(() => getStrings(locale, "copilot_product"), [locale]);
  const productLegal = useMemo(() => getLegalProductContent(locale).refund, [locale]);

  return (
    <article className="break-words text-base leading-7 text-slate-700">
      <section className="pt-8 pb-20 sm:pt-10 sm:pb-24">
        <div className="max-w-4xl">
          <header className="mb-12">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-indigo-600">{t.legal_docs_label}</p>
            <h1 className="mt-4 text-4xl font-black tracking-[-0.04em] text-slate-950 sm:text-6xl">{t.refund_title}</h1>
            <p className="mt-5 text-sm text-slate-500">
              {t.refund_updated} <span className="font-semibold">{t.refund_date}</span>
            </p>
            <p className="mt-6 text-base leading-8 text-slate-600">
              {interpolate(t.refund_intro, { company: "JOBolar.Ai" })}
            </p>
            <p className="mt-3 text-sm leading-6 text-slate-500">{t.refund_disclaimer}</p>
          </header>

          {/* 1. Scope */}
          <section className="mt-10 border-t border-slate-200 pt-8">
            <h2 className="text-2xl font-bold tracking-tight text-slate-950">{t.refund_s1_title}</h2>
            <p className="mt-2 text-slate-700">{t.refund_s1_text}</p>
          </section>

          <section className="mt-10 border-t border-slate-200 pt-8" id="product-usage">
            <h2 className="text-2xl font-bold tracking-tight text-slate-950">{productLegal.title}</h2>
            <p className="mt-3 text-slate-700">{productLegal.intro}</p>
            <ul className="mt-4 list-disc space-y-2 pl-6 text-slate-700">
              {productLegal.usage.map((item) => <li key={item}>{item}</li>)}
            </ul>
            <p className="mt-5 text-slate-700">{productLegal.law}</p>
          </section>

          {/* 2. Key Definitions */}
          <section className="mt-10 border-t border-slate-200 pt-8">
            <h2 className="text-2xl font-bold tracking-tight text-slate-950">{t.refund_s2_title}</h2>
            <ul className="mt-2 list-disc ml-6 text-slate-700 space-y-2">
              <li><span className="font-medium">{t.refund_s2_li1_label}</span> {t.refund_s2_li1}</li>
              <li><span className="font-medium">{t.refund_s2_li2_label}</span> {t.refund_s2_li2}</li>
            </ul>
          </section>

          {/* 3. Free Trials & Promotions */}
          <section className="mt-10 border-t border-slate-200 pt-8">
            <h2 className="text-2xl font-bold tracking-tight text-slate-950">{t.refund_s3_title}</h2>
            <ul className="mt-2 list-disc ml-6 text-slate-700 space-y-2">
              <li>{t.refund_s3_li1}</li>
              <li>{t.refund_s3_li2}</li>
              <li>{t.refund_s3_li3}</li>
            </ul>
          </section>

          {/* 4. Cooling‑Off & Local Rights */}
          <section className="mt-10 border-t border-slate-200 pt-8">
            <h2 className="text-2xl font-bold tracking-tight text-slate-950">{t.refund_s4_title}</h2>
            <p className="mt-2 text-slate-700">{t.refund_s4_text}</p>
          </section>

          {/* 5. Standard Refund Window */}
          <section className="mt-10 border-t border-slate-200 pt-8">
            <h2 className="text-2xl font-bold tracking-tight text-slate-950">{t.refund_s5_title}</h2>
            <ul className="mt-2 list-disc ml-6 text-slate-700 space-y-2">
              <li>{t.refund_s5_li1}</li>
              <li>{t.refund_s5_li2}</li>
              <li>{t.refund_s5_li3}</li>
            </ul>
          </section>

          {/* 6. Renewals, Upgrades, Downgrades */}
          <section className="mt-10 border-t border-slate-200 pt-8">
            <h2 className="text-2xl font-bold tracking-tight text-slate-950">{t.refund_s6_title}</h2>
            <ul className="mt-2 list-disc ml-6 text-slate-700 space-y-2">
              <li><span className="font-medium">{t.refund_s6_li1_label}</span> {t.refund_s6_li1}</li>
              <li><span className="font-medium">{t.refund_s6_li2_label}</span> {t.refund_s6_li2}</li>
              <li><span className="font-medium">{t.refund_s6_li3_label}</span> {t.refund_s6_li3}</li>
              <li><span className="font-medium">{t.refund_s6_li4_label}</span> {t.refund_s6_li4}</li>
            </ul>
          </section>

          {/* 7. Non‑Refundable Items */}
          <section className="mt-10 border-t border-slate-200 pt-8">
            <h2 className="text-2xl font-bold tracking-tight text-slate-950">{t.refund_s7_title}</h2>
            <ul className="mt-2 list-disc ml-6 text-slate-700 space-y-2">
              <li>{t.refund_s7_li1}</li>
              <li>{t.refund_s7_li2}</li>
              <li>{t.refund_s7_li3}</li>
              <li>{t.refund_s7_li4}</li>
            </ul>
          </section>

          {/* AI Interview Assistant Purchases */}
          <section className="mt-10 border-t border-slate-200 pt-8" id="interview-copilot-refunds">
            <h2 className="text-2xl font-bold tracking-tight text-slate-950">{tc.refund_copilot_title}</h2>
            <ul className="mt-2 list-disc ml-6 text-slate-700 space-y-2">
              {Array.from({ length: 5 }, (_, index) => (
                <li key={index}>{tc[`refund_copilot_li${index + 1}`]}</li>
              ))}
            </ul>
          </section>

          {/* 8. How to Request a Refund */}
          <section className="mt-10 border-t border-slate-200 pt-8">
            <h2 className="text-2xl font-bold tracking-tight text-slate-950">{t.refund_s8_title}</h2>
            <ol className="mt-2 list-decimal ml-6 text-slate-700 space-y-2">
              <li>{interpolate(t.refund_s8_li1, { email: "billing@JOBolar.Ai" })}</li>
              <li>{t.refund_s8_li2}</li>
              <li>{t.refund_s8_li3}</li>
            </ol>
            <p className="mt-2 text-slate-700">{t.refund_s8_processing}</p>
          </section>

          {/* 8A. Payment Provider Processing */}
          <section className="mt-10 border-t border-slate-200 pt-8">
            <h2 className="text-2xl font-bold tracking-tight text-slate-950">{t.refund_s8a_title}</h2>
            <p className="mt-2 text-slate-700">{t.refund_s8a_intro}</p>
            <ul className="mt-2 list-disc ml-6 text-slate-700 space-y-2">
              <li><span className="font-medium">{t.refund_s8a_li1_label}</span> {t.refund_s8a_li1}</li>
              <li><span className="font-medium">{t.refund_s8a_li2_label}</span> {t.refund_s8a_li2}</li>
            </ul>
            <p className="mt-2 text-slate-700">{t.refund_s8a_disclaimer}</p>
          </section>

          {/* 9. Cancellations */}
          <section className="mt-10 border-t border-slate-200 pt-8">
            <h2 className="text-2xl font-bold tracking-tight text-slate-950">{t.refund_s9_title}</h2>
            <ul className="mt-2 list-disc ml-6 text-slate-700 space-y-2">
              <li>{t.refund_s9_li1}</li>
              <li>{t.refund_s9_li2}</li>
              <li>{t.refund_s9_li3}</li>
            </ul>
          </section>

          {/* 10. Disputed Charges & Chargebacks */}
          <section className="mt-10 border-t border-slate-200 pt-8">
            <h2 className="text-2xl font-bold tracking-tight text-slate-950">{t.refund_s10_title}</h2>
            <ul className="mt-2 list-disc ml-6 text-slate-700 space-y-2">
              <li>{interpolate(t.refund_s10_li1, { email: "billing@JOBolar.Ai" })}</li>
              <li>{t.refund_s10_li2}</li>
            </ul>
          </section>

          {/* 11. Compliance & Exceptions */}
          <section className="mt-10 border-t border-slate-200 pt-8">
            <h2 className="text-2xl font-bold tracking-tight text-slate-950">{t.refund_s11_title}</h2>
            <ul className="mt-2 list-disc ml-6 text-slate-700 space-y-2">
              <li>{t.refund_s11_li1}</li>
              <li>{t.refund_s11_li2}</li>
            </ul>
          </section>

          {/* 12. Changes & Contact */}
          <section className="mt-10 border-t border-slate-200 pt-8">
            <h2 className="text-2xl font-bold tracking-tight text-slate-950">{t.refund_s12_title}</h2>
            <p className="mt-2 text-slate-700">{t.refund_s12_text}</p>
            <p className="mt-2 text-slate-700">
              {interpolate(t.refund_s12_contact, { billing_email: "billing@JOBolar.Ai", support_email: "support@JOBolar.Ai" })}
            </p>
          </section>
        </div>
      </section>
    </article>
  );
}
